import type { ApplyRequest, ApplyResult, Proposal, SectionDecision, SectionOutcome } from '@/lib/templates/api';

export type Choice = 'keep' | 'use';

export interface ReviewState {
  proposalId: string;
  round: 'deciding' | 'conflicts' | 'closed';
  choices: Record<string, Choice>;
  outcomes: Record<string, SectionOutcome>;
  conflictChoices: Record<string, 'keep' | 'force'>;
  /** Sections the server kept conflicted even when forced: only Keep is left. */
  unforceable: Record<string, true>;
}

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _removed, ...rest } = record;
  return rest;
}

export function startReview(proposal: Proposal): ReviewState {
  const outcomes: Record<string, SectionOutcome> = {};
  for (const { key, outcome } of proposal.sections) if (outcome) outcomes[key] = outcome;
  return {
    proposalId: proposal.proposalId,
    round: Object.values(outcomes).includes('conflicted') ? 'conflicts' : 'deciding',
    choices: {},
    outcomes,
    conflictChoices: {},
    unforceable: {},
  };
}

export function choose(state: ReviewState, key: string, choice: Choice | null): ReviewState {
  return { ...state, choices: choice === null ? without(state.choices, key) : { ...state.choices, [key]: choice } };
}

export function chooseAll(state: ReviewState, proposal: Proposal, choice: Choice): ReviewState {
  const choices = { ...state.choices };
  for (const section of proposal.sections) {
    if (section.changed) choices[section.key] = choice;
  }
  return { ...state, choices };
}

export function chooseConflict(state: ReviewState, key: string, choice: 'keep' | 'force' | null): ReviewState {
  if (choice === 'force' && state.unforceable[key]) return state;
  return {
    ...state,
    conflictChoices: choice === null ? without(state.conflictChoices, key) : { ...state.conflictChoices, [key]: choice },
  };
}

export function applyBody(state: ReviewState, proposal: Proposal): ApplyRequest {
  const decisions: SectionDecision[] = [];
  for (const section of proposal.sections) {
    const outcome = state.outcomes[section.key];
    if (state.round === 'conflicts' && outcome === 'conflicted') {
      decisions.push(
        state.conflictChoices[section.key] === 'force'
          ? { key: section.key, accept: true, force: true }
          : { key: section.key, accept: false },
      );
    } else if (outcome === undefined) {
      const choice = state.choices[section.key];
      if (!section.changed || choice === undefined) continue;
      decisions.push({ key: section.key, accept: choice === 'use' });
    }
  }
  return { proposalId: proposal.proposalId, decisions };
}

export function withResult(state: ReviewState, result: ApplyResult): ReviewState {
  const outcomes = { ...state.outcomes };
  for (const { key, outcome } of result.sections) outcomes[key] = outcome;
  if (result.state === 'closed') return { ...state, round: 'closed', outcomes };
  const conflictChoices: ReviewState['conflictChoices'] = {};
  const unforceable: ReviewState['unforceable'] = {};
  for (const [key, choice] of Object.entries(state.conflictChoices)) {
    if (outcomes[key] !== 'conflicted') continue;
    if (choice === 'force') unforceable[key] = true;
    else conflictChoices[key] = choice;
  }
  for (const key of Object.keys(state.unforceable)) {
    if (outcomes[key] === 'conflicted') unforceable[key] = true;
  }
  return { ...state, round: 'conflicts', outcomes, conflictChoices, unforceable };
}

export function counts(
  state: ReviewState,
  proposal: Proposal,
): { changed: number; total: number; applied: number; conflicted: number } {
  const outcomes = Object.values(state.outcomes);
  return {
    changed: proposal.sections.filter((section) => section.changed).length,
    total: proposal.sections.length,
    applied: outcomes.filter((outcome) => outcome === 'applied').length,
    conflicted: outcomes.filter((outcome) => outcome === 'conflicted').length,
  };
}
